import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, User, Tag, ArrowLeft, Clock, Home, Search, FileText, Book, Globe, Server } from 'lucide-react';
import { useContent } from '../contexts/SupabaseContentContext';
import { format } from 'date-fns';

const BlogPost: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { blogPosts, getCategory, loading: contentLoading } = useContent();
  const [loading, setLoading] = useState(true);

  const post = blogPosts.find(p => p.slug === slug && p.published);

  // Set canonical URL and page title
  useEffect(() => {
    if (post) {
      // Remove existing canonical link
      const existingCanonical = document.querySelector('link[rel="canonical"]');
      if (existingCanonical) {
        existingCanonical.remove();
      }

      // Add new canonical link
      const canonical = document.createElement('link');
      canonical.rel = 'canonical';
      canonical.href = `https://vmon.vn/${post.slug}`;
      document.head.appendChild(canonical);

      // Set page title
      document.title = `${post.title} - VMon Cloud`;

      // Set meta description
      let metaDescription = document.querySelector('meta[name="description"]');
      if (!metaDescription) {
        metaDescription = document.createElement('meta');
        metaDescription.setAttribute('name', 'description');
        document.head.appendChild(metaDescription);
      }
      metaDescription.setAttribute('content', post.excerpt);

      // Cleanup function
      return () => {
        if (canonical.parentNode) {
          canonical.parentNode.removeChild(canonical);
        }
        document.title = 'VMon Cloud - Dịch vụ VPS và Hosting chất lượng cao';
        if (metaDescription) {
          metaDescription.setAttribute('content', 'VMon Cloud - Dịch vụ VPS và hosting chất lượng cao');
        }
      };
    }
  }, [post]);

  // Set loading state based on content loading
  useEffect(() => {
    if (!contentLoading) {
      // Add a small delay to ensure smooth transition
      const timer = setTimeout(() => {
        setLoading(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [contentLoading]);

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Đang tải bài viết...</p>
        </div>
      </div>
    );
  }

  if (!post) {
    const popularPages = [
      {
        title: 'VPS US - Mỹ',
        description: 'VPS giá rẻ tại Mỹ với hiệu suất cao',
        path: '/vps-us',
        icon: Globe
      },
      {
        title: 'VPS Singapore',
        description: 'VPS tốc độ cao tại Singapore',
        path: '/vps-singapore',
        icon: Globe
      },
      {
        title: 'VPS Windows',
        description: 'VPS Windows Server chuyên nghiệp',
        path: '/vps-windows',
        icon: Server
      },
      {
        title: 'Blog',
        description: 'Kiến thức về VPS và hosting',
        path: '/blog',
        icon: Book
      },
      {
        title: 'Tài liệu',
        description: 'Hướng dẫn sử dụng VPS',
        path: '/docs',
        icon: FileText
      },
      {
        title: 'Giới thiệu',
        description: 'Thông tin về VMON Cloud',
        path: '/about',
        icon: FileText
      }
    ];

    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            {/* 404 Animation */}
            <div className="relative mb-8">
              <div className="text-9xl md:text-[12rem] font-bold text-blue-100 select-none">
                404
              </div>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-32 h-32 bg-blue-600 rounded-full flex items-center justify-center animate-pulse">
                  <Search className="h-16 w-16 text-white" />
                </div>
              </div>
            </div>

            {/* Error Message */}
            <div className="mb-12">
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                Không tìm thấy bài viết
              </h1>
              <p className="text-xl text-gray-600 mb-2">
                Xin lỗi, bài viết bạn đang tìm kiếm không tồn tại hoặc đã bị xóa.
              </p>
              <p className="text-lg text-gray-500">
                Hãy kiểm tra lại URL hoặc sử dụng các liên kết bên dưới để tiếp tục.
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
              <Link
                to="/"
                className="inline-flex items-center px-8 py-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold text-lg shadow-lg hover:shadow-xl transform hover:-translate-y-1"
              >
                <Home className="mr-3 h-6 w-6" />
                Về trang chủ
              </Link>
              
              <Link
                to="/blog"
                className="inline-flex items-center px-8 py-4 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors font-semibold text-lg shadow-lg hover:shadow-xl transform hover:-translate-y-1"
              >
                <Book className="mr-3 h-6 w-6" />
                Xem tất cả bài viết
              </Link>
            </div>

            {/* Popular Pages */}
            <div className="max-w-6xl mx-auto">
              <h2 className="text-2xl font-bold text-gray-900 mb-8">
                Có thể bạn đang tìm kiếm
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {popularPages.map((page, index) => (
                  <Link
                    key={index}
                    to={page.path}
                    className="group bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 p-6 border border-gray-100 hover:border-blue-200 transform hover:-translate-y-2"
                  >
                    <div className="flex items-center mb-4">
                      <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center group-hover:bg-blue-600 transition-colors">
                        <page.icon className="h-6 w-6 text-blue-600 group-hover:text-white transition-colors" />
                      </div>
                      <div className="ml-4">
                        <h3 className="text-lg font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
                          {page.title}
                        </h3>
                      </div>
                    </div>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      {page.description}
                    </p>
                    <div className="mt-4 flex items-center text-blue-600 font-medium text-sm">
                      <span>Xem chi tiết</span>
                      <ArrowLeft className="ml-2 h-4 w-4 rotate-180 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            {/* Help Section */}
            <div className="mt-16 bg-white rounded-2xl shadow-lg p-8 max-w-4xl mx-auto">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Cần hỗ trợ?</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl">📞</span>
                  </div>
                  <h4 className="font-semibold text-gray-900 mb-2">Hotline</h4>
                  <p className="text-gray-600 text-sm mb-3">Hỗ trợ 24/7</p>
                  <a 
                    href="tel:+84966805315" 
                    className="text-blue-600 hover:text-blue-800 font-medium"
                  >
                    0966.805.315
                  </a>
                </div>
                
                <div className="text-center">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl">💬</span>
                  </div>
                  <h4 className="font-semibold text-gray-900 mb-2">Live Chat</h4>
                  <p className="text-gray-600 text-sm mb-3">Trò chuyện trực tiếp</p>
                  <a 
                    href="https://m.me/VMonCloud" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 font-medium"
                  >
                    Messenger
                  </a>
                </div>
                
                <div className="text-center">
                  <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl">📧</span>
                  </div>
                  <h4 className="font-semibold text-gray-900 mb-2">Email</h4>
                  <p className="text-gray-600 text-sm mb-3">Gửi yêu cầu hỗ trợ</p>
                  <a 
                    href="mailto:support@vmon.vn" 
                    className="text-blue-600 hover:text-blue-800 font-medium"
                  >
                    support@vmon.vn
                  </a>
                </div>
              </div>
            </div>

            {/* Search Suggestion */}
            <div className="mt-12 bg-gradient-to-r from-blue-600 to-blue-800 rounded-2xl text-white p-8 max-w-2xl mx-auto">
              <h3 className="text-xl font-bold mb-4">Tìm kiếm bài viết</h3>
              <p className="mb-6 opacity-90">
                Sử dụng tính năng tìm kiếm để tìm bài viết về VPS, hosting và các dịch vụ khác
              </p>
              <div className="flex">
                <input
                  type="text"
                  placeholder="Nhập từ khóa tìm kiếm..."
                  className="flex-1 px-4 py-3 rounded-l-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-300"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      const searchTerm = (e.target as HTMLInputElement).value;
                      if (searchTerm.trim()) {
                        window.location.href = `/blog?search=${encodeURIComponent(searchTerm)}`;
                      }
                    }
                  }}
                />
                <button 
                  className="px-6 py-3 bg-white text-blue-600 rounded-r-lg hover:bg-blue-50 transition-colors font-semibold"
                  onClick={() => {
                    const input = document.querySelector('input[placeholder="Nhập từ khóa tìm kiếm..."]') as HTMLInputElement;
                    const searchTerm = input?.value;
                    if (searchTerm?.trim()) {
                      window.location.href = `/blog?search=${encodeURIComponent(searchTerm)}`;
                    }
                  }}
                >
                  <Search className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const category = getCategory(post.category_id);
  const relatedPosts = blogPosts
    .filter(p => p.id !== post.id && p.published && (
      // Ưu tiên cùng danh mục
      p.category_id === post.category_id || 
      // Hoặc có chung tag
      p.tags.some(tag => post.tags.includes(tag))
    ))
    .slice(0, 3);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Link
            to="/blog"
            className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Quay lại Blog
          </Link>
        </div>
      </div>

      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Featured Image */}
        <div className="mb-8">
          <img
            src={post.featured_image}
            alt={post.title}
            className="w-full h-64 md:h-96 object-cover rounded-lg shadow-lg"
          />
        </div>

        {/* Article Header */}
        <header className="mb-8">
          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 mb-4">
            <div className="flex items-center">
              <Calendar className="h-4 w-4 mr-1" />
              {format(new Date(post.created_at), 'dd/MM/yyyy')}
            </div>
            <div className="flex items-center">
              <User className="h-4 w-4 mr-1" />
              {post.author}
            </div>
            <div className="flex items-center">
              <Clock className="h-4 w-4 mr-1" />
              5 phút đọc
            </div>
            {category && (
              <Link 
                to={`/category/${category.slug}`}
                className="bg-blue-100 text-blue-800 text-sm font-medium px-3 py-1 rounded-full hover:bg-blue-200 transition-colors"
              >
                {category.name}
              </Link>
            )}
          </div>

          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {post.title}
          </h1>

          <p className="text-xl text-gray-600 mb-6">
            {post.excerpt}
          </p>

          <div className="flex flex-wrap gap-2">
            {post.tags.map(tag => (
              <span key={tag} className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                <Tag className="h-3 w-3 mr-1" />
                {tag}
              </span>
            ))}
          </div>
        </header>

        {/* Article Content */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
          <div 
            className="prose prose-lg max-w-none"
            dangerouslySetInnerHTML={{ __html: post.content }}
          />
        </div>

        {/* Related Posts */}
        {relatedPosts.length > 0 && (
          <section className="bg-white rounded-lg shadow-lg p-8 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Bài viết liên quan</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {relatedPosts.map(relatedPost => (
                <article key={relatedPost.id} className="border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow">
                  <img
                    src={relatedPost.featured_image}
                    alt={relatedPost.title}
                    className="w-full h-32 object-cover"
                  />
                  <div className="p-4">
                    <h3 className="font-semibold text-gray-900 mb-2 line-clamp-2">
                      <Link to={`/${relatedPost.slug}`} className="hover:text-blue-600">
                        {relatedPost.title}
                      </Link>
                    </h3>
                    <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                      {relatedPost.excerpt}
                    </p>
                    <div className="text-xs text-gray-500">
                      {format(new Date(relatedPost.created_at), 'dd/MM/yyyy')}
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </section>
        )}

        {/* Help Section */}
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Cần hỗ trợ thêm?</h3>
          <p className="text-gray-600 mb-4">
            Nếu bạn gặp khó khăn hoặc có thắc mắc, đội ngũ kỹ thuật của chúng tôi sẵn sàng hỗ trợ 24/7.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <a
              href="mailto:support@vmon.vn"
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-center"
            >
              Gửi email hỗ trợ
            </a>
            <a
              href="tel:+84966805315"
              className="border border-blue-600 text-blue-600 px-4 py-2 rounded-lg hover:bg-blue-50 transition-colors text-center"
            >
              Gọi điện hỗ trợ
            </a>
          </div>
        </div>
      </article>

      {/* CSS cho YouTube iframe responsive */}
      <style dangerouslySetInnerHTML={{
        __html: `
          .prose iframe[src*="youtube.com/embed"] {
            width: 100%;
            max-width: 560px;
            height: 315px;
            border-radius: 8px;
            margin: 16px 0;
          }
          
          @media (max-width: 640px) {
            .prose iframe[src*="youtube.com/embed"] {
              height: 200px;
            }
          }
        `
      }} />
    </div>
  );
};

export default BlogPost;